import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton.tsx';
import styles from './ScreenFrame.module.css';

export interface ScreenFrameProps {
  title: ReactNode;
  /** Where the back button goes; defaults to the previous page. */
  backTo?: string;
  actions?: ReactNode;
  /** `blueprint` is the planning look used by Play and Codex screens. */
  tone?: 'night' | 'blueprint';
  children: ReactNode;
}

/** Standard sub-screen: safe-area padding, back button, title, scrolling content. */
export function ScreenFrame({
  title,
  backTo,
  actions,
  tone = 'night',
  children,
}: ScreenFrameProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className={[styles['frame'], styles[tone]].join(' ')}>
      <header className={styles['header']}>
        <IconButton
          icon="back"
          label={t('back')}
          onClick={() => {
            if (backTo) void navigate(backTo);
            else void navigate(-1);
          }}
        />
        <h1 className={styles['title']}>{title}</h1>
        <div className={styles['actions']}>{actions}</div>
      </header>
      <main id="main" tabIndex={-1} className={styles['content']}>
        {children}
      </main>
    </div>
  );
}
